import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { Card, CardContent } from '../../../components/ui/Card';
import Badge from '../../../components/ui/Badge';

const TenantCard = ({ tenant, onEdit, onSetRiskLevel }) => {
  const getRiskVariant = (level) => {
    switch (level) {
      case 'high': return 'danger';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })?.format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isExpiringWithin60Days = (endDate) => {
    const today = new Date();
    const lockInEnd = new Date(endDate);
    const diffTime = lockInEnd - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 60 && diffDays > 0;
  };

  return (
    <Card hover shadow="md" className="border border-border">
      <CardContent className="p-4 md:p-6">
        <div className="flex flex-col space-y-4">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <h3 className="text-h5 font-heading font-semibold text-foreground truncate">
                {tenant?.name}
              </h3>
              <p className="text-body-sm text-muted-foreground truncate">
                {tenant?.company}
              </p>
            </div>
            <Badge variant={getRiskVariant(tenant?.riskLevel)} size="sm">
              {tenant?.riskLevel?.charAt(0)?.toUpperCase() + tenant?.riskLevel?.slice(1)} Risk
            </Badge>
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-muted rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <Icon name="Users" size={16} className="text-muted-foreground" />
                <span className="text-caption text-muted-foreground">Seats</span>
              </div>
              <p className="text-h4 font-heading font-semibold text-foreground mt-1">
                {tenant?.seatsTaken}
              </p>
            </div>
            <div className="bg-muted rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <Icon name="IndianRupee" size={16} className="text-muted-foreground" />
                <span className="text-caption text-muted-foreground">Monthly</span>
              </div>
              <p className="text-h4 font-heading font-semibold text-foreground mt-1">
                {formatCurrency(tenant?.monthlyRevenue)}
              </p>
            </div>
          </div>

          {/* Lock-in End Date */}
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Icon name="Calendar" size={16} className="text-gray-500" />
                <span className="text-xs text-gray-500">Lock-in Ends</span>
              </div>
              {isExpiringWithin60Days(tenant?.lockinEndDate) && (
                <Icon name="AlertTriangle" size={16} className="text-amber-500" />
              )}
            </div>
            <p className={`text-sm font-medium mt-1 ${
              isExpiringWithin60Days(tenant?.lockinEndDate) 
                ? 'text-amber-600' :'text-gray-900'
            }`}>
              {formatDate(tenant?.lockinEndDate)}
            </p>
          </div>

          {/* Actions */}
          <div className="flex space-x-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEdit(tenant)}
              iconName="Edit"
              iconPosition="left"
              className="flex-1"
            >
              Edit
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onSetRiskLevel(tenant)}
              iconName="AlertCircle"
              iconPosition="left"
              className="flex-1"
            >
              Risk Level
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TenantCard;